import { adminPath, useScope } from "../lib/scope";
import {
  AlertIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleSlashIcon,
  KebabHorizontalIcon,
  KeyIcon,
  RocketIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  RepoIcon,
  ServerIcon,
  SyncIcon,
  TrashIcon,
} from "@primer/octicons-react";
import { useState } from "react";
import { HttpError } from "../api/client";
import { useAction, useInstallInfo, useRepos, useRunners } from "../api/hooks";
import type { RegisteredRunner, RunnerPlacement, RunnerPlatform, RunnerSeen } from "../api/types";
import { CopyButton, Empty, ErrorMessage, LiveDot, Pre, TimeAgo } from "../components/common";
import { timeAgo } from "../components/format";
import { Alert, AlertTitle } from "../components/ui/alert";
import { Badge, type Tone } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardHeader } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Field, FieldGrid } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { ChoiceCard, ChoiceGroup } from "../components/ui/radio-group";
import { Select } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tooltip } from "../components/ui/tooltip";
import { cn } from "../lib/utils";
import { deployable, Deployment, PLATFORM_LABELS, platformLabel } from "./RunnerDeploy";

/** A runner polls about once a second; one quiet for this long is offline. */
export const LIVE_MS = 60_000;
/** GitLab's "stale": no contact for a week (instance rows are pruned then too). */
const STALE_MS = 7 * 24 * 3600_000;
export const isLive = (r: RunnerSeen) => Date.now() - r.last_seen < LIVE_MS;

/** Mirrors runners.nameRe and its reserved route names. */
const NAME_RE = /^[a-z0-9]([a-z0-9._-]{0,62}[a-z0-9])?$/;
const RESERVED = ["placements", "setup", "usage", "limits"];

const EXPIRY = [
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "1 year" },
  { value: "0", label: "Never" },
];

type Status = { label: string; tone: Tone; hint: string };

/** GitLab's runner statuses, plus GitHub's idle/active split for online runners. */
export function runnerStatus(r: RegisteredRunner, instances: RunnerSeen[]): Status {
  const now = Date.now();
  if (r.token_expires && r.token_expires < now)
    return { label: "Token expired", tone: "red", hint: "Its instances cannot authenticate. Reset the token and deploy the new one." };
  if (r.paused) return { label: "Paused", tone: "yellow", hint: "Its instances keep polling but get no CI jobs until you resume it." };
  if (!r.contacted) return { label: "Never contacted", tone: "default", hint: "No instance has polled with this token yet." };
  const live = instances.filter(isLive);
  if (live.length) {
    const busy = live.reduce((n, i) => n + i.busy, 0);
    return busy
      ? { label: "Active", tone: "green", hint: `${busy} CI job${busy === 1 ? "" : "s"} running on ${live.length} instance${live.length === 1 ? "" : "s"}.` }
      : { label: "Idle", tone: "green", hint: `${live.length} instance${live.length === 1 ? "" : "s"} online, waiting for CI jobs.` };
  }
  if (now - r.contacted > STALE_MS) return { label: "Stale", tone: "orange", hint: "No contact for over a week. Delete it if it is gone for good." };
  return { label: "Offline", tone: "default", hint: "No instance has polled in the last minute." };
}

const scopeLabel = (repos: string[]) => (repos.length ? repos.join(", ") : "All repositories");

/** Splits a validation error's "field: message" issues by field. */
function issuesOf(error: unknown): Partial<Record<string, string>> {
  const issues = error instanceof HttpError ? (error.body.detail as { issues?: unknown } | undefined)?.issues : undefined;
  const out: Partial<Record<string, string>> = {};
  if (Array.isArray(issues))
    for (const issue of issues) {
      const [field, ...msg] = String(issue).split(": ");
      if (field && msg.length) out[field] ??= msg.join(": ");
    }
  return out;
}

const COLUMNS = 3;

/** One table, as GitLab's runner list: a row per registered runner, its instances folded under it. */
export function RegisteredRunners({
  registered,
  instances,
  placements,
  strict,
  onNew,
  footer,
}: {
  registered: RegisteredRunner[];
  instances: RunnerSeen[];
  placements: RunnerPlacement[];
  strict: boolean;
  onNew: () => void;
  footer?: React.ReactNode;
}) {
  const enforced = strict && placements.length ? placements.map((p) => p.platform) : undefined;
  const [revealed, setRevealed] = useState<{ runner: RegisteredRunner; token: string } | null>(null);
  const of = (r: RegisteredRunner) => instances.filter((i) => i.runner_id === r.id);
  const known = new Set(registered.map((r) => r.id));
  const unregistered = instances.filter((i) => !known.has(i.runner_id));
  const signed = unregistered.some(isLive) ? unregistered : [];
  const online = registered.filter((r) => of(r).some(isLive)).length;
  return (
    <>
      {revealed && <TokenReveal runner={revealed.runner} token={revealed.token} reset onDone={() => setRevealed(null)} />}
      <Card className="mt-0">
        <CardHeader>
          <ServerIcon /> Runners
          {registered.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {online} of {registered.length} online
            </span>
          )}
          <Button size="sm" variant="primary" className="ml-auto" onClick={onNew}>
            <PlusIcon /> New runner
          </Button>
        </CardHeader>
        {registered.length || signed.length ? (
          <Table>
            <TableHeader>
              <tr>
                <TableHead>Runner</TableHead>
                <TableHead className="w-44">Status</TableHead>
                <TableHead className="w-12" />
              </tr>
            </TableHeader>
            <TableBody>
              {registered.map((r) => (
                <RunnerRow key={r.id} r={r} instances={of(r)} enforced={enforced} onToken={(token, runner) => setRevealed({ runner, token })} />
              ))}
              {signed.length > 0 && <SignedTokenRow instances={signed} enforced={enforced} />}
            </TableBody>
          </Table>
        ) : (
          <Empty icon={ServerIcon}>
            <p>No runners of your own yet. Add one to run CI on your own machine, a local kind cluster, Google Cloud or Cloudflare.</p>
            <Button variant="primary" className="mt-3" onClick={onNew}>
              <PlusIcon /> New runner
            </Button>
          </Empty>
        )}
        {footer}
      </Card>
    </>
  );
}

function ExpandButton({ open, onToggle, label, disabled }: { open: boolean; onToggle: () => void; label: string; disabled?: boolean }) {
  if (disabled) return <span className="-ml-2 inline-block size-7 shrink-0" />;
  return (
    <Button
      type="button"
      size="icon-sm"
      variant="ghost"
      className="-ml-2"
      aria-expanded={open}
      aria-label={`${open ? "Hide" : "Show"} instances of ${label}`}
      onClick={onToggle}
    >
      {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
    </Button>
  );
}

function instanceSummary(instances: RunnerSeen[]) {
  if (!instances.length) return "";
  const live = instances.filter(isLive).length;
  return `${instances.length} instance${instances.length === 1 ? "" : "s"}, ${live} online`;
}

/** The muted line under a runner's name: its parts joined with " · ", empty parts dropped. */
function Meta({ parts }: { parts: React.ReactNode[] }) {
  const shown = parts.filter(Boolean);
  if (!shown.length) return null;
  return (
    <div className="mt-0.5 truncate pl-7 text-xs text-muted-foreground">
      {shown.map((p, i) => (
        <span key={i}>
          {i > 0 && " · "}
          {p}
        </span>
      ))}
    </div>
  );
}

/** Instances using signed runner tokens (bee admin token --kind runner), grouped like one runner. */
function SignedTokenRow({ instances, enforced }: { instances: RunnerSeen[]; enforced?: RunnerPlatform[] }) {
  const [open, setOpen] = useState(false);
  const live = instances.filter(isLive);
  const last = Math.max(...instances.map((i) => i.last_seen));
  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2 text-xs">
            <ExpandButton open={open} onToggle={() => setOpen(!open)} label="signed-token runners" />
            <LiveDot tone={live.length ? "green" : "default"} /> <b>Signed tokens</b>
          </div>
          <Meta parts={[instanceSummary(instances)]} />
        </TableCell>
        <TableCell>
          <Tooltip content="Issued with bee admin token --kind runner. Bee cannot pause or revoke them before they expire; register a runner and move these instances to its token.">
            <Badge tone="yellow">Not revocable</Badge>
          </Tooltip>
          <div className="mt-0.5 text-xs text-muted-foreground">
            <TimeAgo ms={last} />
          </div>
        </TableCell>
        <TableCell />
      </TableRow>
      {open && <InstanceRows instances={instances} enforced={enforced} signed />}
    </>
  );
}

const platformName = (p: string) => (p ? platformLabel(p) : "not reported");

/** Labels beyond the os:, arch: and platform: ones the row already shows; older runners report none. */
const extraLabels = (i: RunnerSeen) => (i.labels ?? []).filter((l) => !/^(os|arch|platform):/.test(l));

/** Why the server gives an instance no CI jobs, mirroring runners.registeredAdmits and admits; "" when it does. */
function heldReason(i: RunnerSeen, reg: RegisteredRunner | undefined, enforced?: RunnerPlatform[]): string {
  if (reg?.paused) return `Runner ${reg.name} is paused.`;
  if (reg?.platform && i.platform !== reg.platform)
    return `Runner ${reg.name} only takes CI jobs on ${platformName(reg.platform)}; start this instance with --platform ${reg.platform}.`;
  if (enforced && !enforced.includes(i.platform as RunnerPlatform)) return `Only runners on ${enforced.map(platformName).join(", ")} get CI jobs.`;
  return "";
}

function InstanceRows({
  instances,
  reg,
  enforced,
  signed,
}: {
  instances: RunnerSeen[];
  reg?: RegisteredRunner;
  enforced?: RunnerPlatform[];
  signed?: boolean;
}) {
  return (
    <TableRow className="hover:[&>td]:bg-transparent">
      <TableCell colSpan={COLUMNS} className="border-t-0 pb-3 pl-10 pt-0">
        {instances.length ? (
          <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <tr>
                  <TableHead>Instance</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead className="max-lg:hidden">Version</TableHead>
                  <TableHead className="max-md:hidden">Address</TableHead>
                  <TableHead>Jobs</TableHead>
                  <TableHead>Last seen</TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {[...instances]
                  .sort((a, b) => b.last_seen - a.last_seen)
                  .map((i) => {
                    const held = heldReason(i, reg, enforced);
                    const live = isLive(i);
                    const slash = i.name.indexOf("/");
                    const subject = i.name.slice(0, slash);
                    const instance = i.name.slice(slash + 1);
                    return (
                      <TableRow key={i.name} className={cn(!live && "[&>td]:opacity-60")}>
                        <TableCell className="font-mono text-xs">
                          <LiveDot tone={live ? "green" : "default"} /> {instance}
                          {signed && subject !== instance && <div className="pl-3 text-muted-foreground">token {subject}</div>}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {i.platform ? (
                            <Badge>{platformName(i.platform)}</Badge>
                          ) : (
                            <Tooltip content="Started without --platform. It takes CI jobs unless its runner or enforced placements require a platform.">
                              <span className="cursor-help text-xs text-muted-foreground">Any</span>
                            </Tooltip>
                          )}{" "}
                          {held && (
                            <Tooltip content={held}>
                              <Badge tone="yellow">held back</Badge>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground max-lg:hidden">
                          {i.version || "–"}
                          {i.os && (
                            <span>
                              {" "}
                              · {i.os}/{i.arch}
                            </span>
                          )}
                          {extraLabels(i).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {extraLabels(i).map((l) => (
                                <Badge key={l}>{l}</Badge>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground max-md:hidden">{i.ip || "–"}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs">
                          {!live ? (
                            <span className="text-muted-foreground">offline</span>
                          ) : (
                            <>
                              {i.busy ? <b>{i.busy}</b> : <span className="text-muted-foreground">idle</span>}
                              {i.concurrency > 0 && <span className="text-muted-foreground"> / {i.concurrency}</span>}
                              {i.concurrency > 0 && i.busy >= i.concurrency && (
                                <Tooltip content="Every slot is busy; queued CI jobs wait for a free slot or another runner.">
                                  <Badge tone="orange" className="ml-1.5">
                                    full
                                  </Badge>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          <TimeAgo ms={i.last_seen} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No instance has polled with this token in the last day. Start one with the token
            {reg && deployable(reg.platform) ? (
              <>
                , or see <b>Deploy</b> in the … menu
              </>
            ) : (
              ""
            )}
            .
          </p>
        )}
      </TableCell>
    </TableRow>
  );
}

/** For a runner no instance has used yet: the command that starts one. */
function StartHint({ r }: { r: RegisteredRunner }) {
  const command = `BEE_TOKEN=<token> bee-runner --url ${location.origin}${r.platform ? ` --platform ${r.platform}` : ""}`;
  return (
    <div className="mt-1.5 pl-7 text-xs text-muted-foreground">
      <div>
        Start it with its token{" "}
        <Tooltip content="The token was shown once, when you created the runner. Lost it? Reset the token from the … menu.">
          <span className="cursor-help underline decoration-dotted">(lost it?)</span>
        </Tooltip>
      </div>
      <div className="mt-0.5 flex items-center gap-1">
        <code className="min-w-0 break-words rounded-md bg-neutral-muted px-1.5 py-0.5 font-mono">{command}</code>
        <CopyButton value={command} title="Copy command" />
      </div>
    </div>
  );
}

function RunnerRow({
  r,
  instances,
  enforced,
  onToken,
}: {
  r: RegisteredRunner;
  instances: RunnerSeen[];
  enforced?: RunnerPlatform[];
  onToken: (token: string, runner: RegisteredRunner) => void;
}) {
  const org = useScope();
  const act = useAction();
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<"edit" | "deploy" | "reset" | "delete" | null>(null);
  const close = (open: boolean) => !open && setDialog(null);
  const status = runnerStatus(r, instances);
  const live = instances.filter(isLive);
  const versions = [...new Set(instances.map((i) => i.version).filter(Boolean))];
  const expired = !!r.token_expires && r.token_expires < Date.now();
  const soon = !expired && !!r.token_expires && r.token_expires - Date.now() < 14 * 24 * 3600_000;
  const at = `${adminPath(org, "/runners")}/${encodeURIComponent(r.id)}`;
  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2 font-mono text-xs">
            <ExpandButton open={open} onToggle={() => setOpen(!open)} label={r.name} disabled={!instances.length} />
            <LiveDot tone={live.length ? "green" : "default"} /> <b>{r.name}</b>
            {r.platform && <Badge>{platformLabel(r.platform)}</Badge>}
          </div>
          <Meta
            parts={[
              r.description,
              <span title={scopeLabel(r.repos)} className={cn(r.repos.length && "font-mono")}>
                {scopeLabel(r.repos)}
              </span>,
              instanceSummary(instances),
              versions.length > 0 && <span className="font-mono">{versions.join(", ")}</span>,
              soon && <span className="text-warning">token expires {timeAgo(r.token_expires)}</span>,
            ]}
          />
          {!r.contacted && !expired && <StartHint r={r} />}
          {act.error ? <ErrorMessage error={act.error} /> : null}
        </TableCell>
        <TableCell>
          <Tooltip content={status.hint}>
            <Badge tone={status.tone}>{status.label}</Badge>
          </Tooltip>
          {r.contacted > 0 && (
            <div className="mt-0.5 text-xs text-muted-foreground">
              <TimeAgo ms={r.contacted} />
            </div>
          )}
        </TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" size="icon-sm" variant="ghost" aria-label={`Actions for ${r.name}`} disabled={act.isPending}>
                <KebabHorizontalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                Token <span className="font-mono">beer_••••{r.token_hint}</span>
                <div className={cn(expired && "text-danger", soon && "text-warning")}>
                  {r.token_expires ? `${expired ? "expired" : "expires"} ${new Date(r.token_expires).toLocaleDateString()}` : "never expires"}
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setDialog("edit")}>
                <PencilIcon /> Edit
              </DropdownMenuItem>
              {deployable(r.platform) && (
                <DropdownMenuItem onSelect={() => setDialog("deploy")}>
                  <RocketIcon /> Deploy
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => act.mutate({ method: "PATCH", path: at, body: { paused: !r.paused } })}>
                {r.paused ? (
                  <>
                    <PlayIcon /> Resume
                  </>
                ) : (
                  <>
                    <CircleSlashIcon /> Pause
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setDialog("reset")}>
                <SyncIcon /> Reset token
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onSelect={() => setDialog("delete")}>
                <TrashIcon /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ConfirmDialog
            open={dialog === "reset"}
            onOpenChange={close}
            title={`Reset the token of ${r.name}?`}
            description="A new token replaces the current one, which stops working within seconds. Every instance fails to authenticate until it gets the new token: update the Secret (or Worker secret) and restart the runners."
            confirmLabel="Reset token"
            onConfirm={() => act.mutate({ path: `${at}/token`, body: {} }, { onSuccess: (d) => onToken(String(d.token), d.runner as RegisteredRunner) })}
          />
          <ConfirmDialog
            open={dialog === "delete"}
            onOpenChange={close}
            title={`Delete ${r.name}?`}
            description="Its token is revoked within seconds: running jobs fail to report and are retried elsewhere. This cannot be undone."
            confirmLabel="Delete runner"
            onConfirm={() => act.mutate({ method: "DELETE", path: at, body: {} })}
          />
          {dialog === "edit" && <EditRunnerDialog r={r} onClose={() => setDialog(null)} />}
          {dialog === "deploy" && deployable(r.platform) && (
            <Dialog open onOpenChange={close}>
              <DialogContent className="top-[6vh] max-h-[88vh] w-[min(720px,calc(100vw-32px))] overflow-y-auto px-4 py-3 text-left">
                <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
                  <RocketIcon /> Deploy <span className="font-mono">{r.name}</span> on {platformLabel(r.platform)}
                </DialogTitle>
                <p className="mt-1 text-xs text-muted-foreground">
                  Keep the runner's token in <code>RUNNER_TOKEN</code> before you run these. It was shown once, when you created the runner; lost it? Reset the
                  token from the … menu.
                </p>
                <Deployment platform={r.platform} />
              </DialogContent>
            </Dialog>
          )}
        </TableCell>
      </TableRow>
      {open && <InstanceRows instances={instances} reg={r} enforced={enforced} />}
    </>
  );
}

/* ---------- create / edit ---------- */

/** The choices of the new-runner dialog, as GitHub's runner image picker; "any" is a runner with no platform, and kind is offered only by a from-source server (make dev). */
const WHERE: Array<{ id: RunnerPlatform | "any"; label: string; desc: string }> = [
  { id: "any", label: "Any machine", desc: "Linux or macOS, directly or in Docker." },
  { id: "kind", label: PLATFORM_LABELS.kind, desc: "A kind cluster on this machine." },
  { id: "gcp", label: PLATFORM_LABELS.gcp, desc: "A GKE cluster from deploy/terraform." },
  { id: "cloudflare", label: PLATFORM_LABELS.cloudflare, desc: "Containers started by a Worker cron." },
  { id: "aws", label: PLATFORM_LABELS.aws, desc: "Your own hosts, started with --platform aws." },
  { id: "ubicloud", label: PLATFORM_LABELS.ubicloud, desc: "Your own hosts, started with --platform ubicloud." },
];

type Draft = { name: string; description: string; platform: RunnerPlatform | ""; access: "all" | "selected"; repos: string[]; expires: string };

export function NewRunnerDialog({ open, onOpenChange, initial }: { open: boolean; onOpenChange: (open: boolean) => void; initial?: Partial<Draft> }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[6vh] max-h-[88vh] w-[min(720px,calc(100vw-32px))] overflow-y-auto">
        {open && <NewRunner initial={initial} onDone={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function NewRunner({ initial, onDone }: { initial?: Partial<Draft>; onDone: () => void }) {
  const org = useScope();
  const dev = useInstallInfo().data?.dev;
  const create = useAction();
  const [f, setF] = useState<Draft>({ name: "", description: "", platform: "", access: "all", repos: [], expires: "90", ...initial });
  const set = (patch: Partial<Draft>) => {
    setF({ ...f, ...patch });
    create.reset();
  };
  const created = create.data as { runner: RegisteredRunner; token: string } | undefined;
  if (created) return <TokenReveal runner={created.runner} token={created.token} onDone={onDone} />;

  const issues = issuesOf(create.error);
  const nameBad = f.name !== "" && (!NAME_RE.test(f.name) || RESERVED.includes(f.name));
  const noRepos = f.access === "selected" && f.repos.length === 0;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate({
      path: adminPath(org, "/runners"),
      body: { name: f.name, description: f.description, platform: f.platform, repos: f.access === "all" ? [] : f.repos, expires_days: Number(f.expires) },
    });
  };
  return (
    <form onSubmit={submit} noValidate>
      <div className="border-b border-border-muted px-4 py-3">
        <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
          <ServerIcon /> New runner
        </DialogTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          The runner gets its own token, shown once. Bee keeps only its hash, so you can pause the runner, rotate the token or revoke it at any time.
        </p>
      </div>
      <div className="flex flex-col gap-5 px-4 py-4">
        <ChoiceGroup legend="Where will it run?" value={f.platform || "any"} onValueChange={(p) => set({ platform: p === "any" ? "" : (p as RunnerPlatform) })}>
          {WHERE.filter((w) => w.id !== "kind" || dev).map((w) => (
            <ChoiceCard key={w.id} value={w.id} title={w.label} description={w.desc} />
          ))}
        </ChoiceGroup>
        <FieldGrid className="mb-0">
          <Field
            label="Name"
            required
            invalid={nameBad || !!issues.name}
            hint={
              issues.name
                ? `Name ${issues.name}.`
                : nameBad
                  ? "Lowercase letters, digits, '.', '_' or '-', up to 64; not placement or setup."
                  : "Shown in the job queue and audit log."
            }
          >
            <Input
              mono
              autoFocus
              value={f.name}
              onChange={(e) => set({ name: e.target.value.trim().toLowerCase() })}
              placeholder="gke-spot"
              aria-invalid={nameBad}
              required
            />
          </Field>
          <Field
            label="Description"
            invalid={!!issues.description}
            hint={issues.description ? `Description ${issues.description}.` : "Optional: what or where it is."}
          >
            <Input value={f.description} onChange={(e) => set({ description: e.target.value })} placeholder="Spot pool in us-central1" />
          </Field>
        </FieldGrid>
        <RepoAccess f={f} set={set} error={issues.repos} />
        <FieldGrid className="mb-0">
          <Field
            label="Token expires after"
            hint={f.expires === "0" ? "Rotate it yourself; a leaked token works until you reset it." : "Reset it before then from this page."}
          >
            <Select value={f.expires} onValueChange={(v) => set({ expires: v })} options={EXPIRY} />
          </Field>
        </FieldGrid>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-muted px-4 py-3">
        {create.error && !Object.keys(issues).length ? (
          <div className="min-w-0 flex-1">
            <ErrorMessage error={create.error} />
          </div>
        ) : null}
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" disabled={create.isPending || !f.name || nameBad || noRepos}>
          <KeyIcon /> {create.isPending ? "Creating…" : "Create runner and show setup"}
        </Button>
      </div>
    </form>
  );
}

/** GitHub's runner group repository access: every repository, or a list picked from the real ones. */
function RepoAccess({ f, set, error }: { f: Pick<Draft, "access" | "repos">; set: (p: Partial<Draft>) => void; error?: string }) {
  const repos = useRepos();
  const [filter, setFilter] = useState("");
  const names = (repos.data ?? []).map((r) => r.name);
  const shown = names.filter((n) => n.includes(filter.trim().toLowerCase()));
  const toggle = (n: string) => set({ repos: f.repos.includes(n) ? f.repos.filter((x) => x !== n) : [...f.repos, n].sort() });
  return (
    <div>
      <ChoiceGroup legend="Repository access" value={f.access} onValueChange={(access) => set({ access })}>
        <ChoiceCard value="all" title="All repositories" description="Runs CI for every repository, including ones created later." />
        <ChoiceCard value="selected" title="Selected repositories" description="Runs CI only for these. Its token cannot read other repositories either." />
      </ChoiceGroup>
      {f.access === "selected" && (
        <div className="mt-3 rounded-md border">
          <div className="flex items-center gap-2 border-b border-border-muted p-2">
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={`Filter ${names.length} repositories`} className="h-7" />
            <span className="whitespace-nowrap text-xs text-muted-foreground">{f.repos.length} selected</span>
          </div>
          <div className="max-h-48 overflow-y-auto p-1">
            {repos.isLoading ? (
              <div className="p-2 text-xs text-muted-foreground">Loading repositories…</div>
            ) : repos.error ? (
              <ErrorMessage error={repos.error} />
            ) : shown.length ? (
              shown.map((n) => (
                <label key={n} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-muted">
                  <Checkbox checked={f.repos.includes(n)} onCheckedChange={() => toggle(n)} />
                  <RepoIcon className="text-muted-foreground" size={14} />
                  <span className="font-mono text-xs">{n}</span>
                </label>
              ))
            ) : (
              <div className="p-2 text-xs text-muted-foreground">{names.length ? "No repository matches." : "No repositories yet."}</div>
            )}
          </div>
        </div>
      )}
      {(error || (f.access === "selected" && !f.repos.length)) && (
        <small className="mt-1.5 block text-xs text-danger">{error ? `Repositories: ${error}.` : "Pick at least one repository, or allow all."}</small>
      )}
    </div>
  );
}

function EditRunnerDialog({ r, onClose }: { r: RegisteredRunner; onClose: () => void }) {
  const org = useScope();
  const save = useAction();
  const [f, setF] = useState({
    description: r.description,
    platform: r.platform,
    access: (r.repos.length ? "selected" : "all") as Draft["access"],
    repos: r.repos,
  });
  const set = (p: Partial<Draft>) => {
    setF({ ...f, ...p } as typeof f);
    save.reset();
  };
  const issues = issuesOf(save.error);
  const noRepos = f.access === "selected" && f.repos.length === 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="top-[6vh] max-h-[88vh] overflow-y-auto text-left">
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(
              {
                method: "PATCH",
                path: `${adminPath(org, "/runners")}/${encodeURIComponent(r.id)}`,
                body: { description: f.description, platform: f.platform, repos: f.access === "all" ? [] : f.repos },
              },
              { onSuccess: onClose },
            );
          }}
        >
          <div className="border-b border-border-muted px-4 py-3">
            <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
              <PencilIcon /> Edit <span className="font-mono">{r.name}</span>
            </DialogTitle>
          </div>
          <div className="flex flex-col gap-5 px-4 py-4">
            <FieldGrid className="mb-0">
              <Field
                label="Description"
                invalid={!!issues.description}
                hint={issues.description ? `Description ${issues.description}.` : "What or where it is."}
              >
                <Input value={f.description} onChange={(e) => set({ description: e.target.value })} />
              </Field>
              <Field label="Platform" hint="Changes apply to the next poll of each instance.">
                <Select
                  value={f.platform}
                  onValueChange={(p) => set({ platform: p as RunnerPlatform | "" })}
                  options={[
                    { value: "", label: "Any platform" },
                    ...(Object.keys(PLATFORM_LABELS) as RunnerPlatform[]).map((p) => ({ value: p, label: PLATFORM_LABELS[p] })),
                  ]}
                />
              </Field>
            </FieldGrid>
            <RepoAccess f={f} set={set} error={issues.repos} />
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-border-muted px-4 py-3">
            {save.error && !Object.keys(issues).length ? (
              <div className="min-w-0 flex-1">
                <ErrorMessage error={save.error} />
              </div>
            ) : null}
            <Button type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" disabled={save.isPending || noRepos}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- the token, once ---------- */

/**
 * The token right after create or reset, as GitLab's "Register runner" page:
 * copy it, start the runner with it, and watch the runner connect.
 */
function TokenReveal({ runner, token, reset, onDone }: { runner: RegisteredRunner; token: string; reset?: boolean; onDone: () => void }) {
  const [hidden, setHidden] = useState(true);
  const url = location.origin;
  const flag = runner.platform ? ` --platform ${runner.platform}` : "";
  const shown = hidden ? `${token.slice(0, 9)}${"•".repeat(24)}${token.slice(-4)}` : token;
  const local = (t: string) => `export BEE_URL=${url}\nexport BEE_TOKEN=${t}\nbee-runner${flag}`;
  const docker = (t: string) => `export BEE_TOKEN=${t}\ndocker run -d --name bee-runner -e BEE_URL=${url} -e BEE_TOKEN bee-runner:local${flag}`;
  const cluster = deployable(runner.platform);
  return (
    <div className={reset ? "" : "px-4 py-4"}>
      <Alert variant="success" className="mt-0">
        <AlertTitle>
          <CheckCircleIcon /> {reset ? `New token for ${runner.name}.` : `Runner ${runner.name} created.`} Copy the token now: it is not shown again.
        </AlertTitle>
        <div className="my-3 flex h-8 items-center gap-1 rounded-md border bg-background pl-2 pr-1">
          <code className="min-w-0 flex-1 truncate">{shown}</code>
          <Button type="button" size="sm" variant="ghost" onClick={() => setHidden(!hidden)}>
            {hidden ? "Show" : "Hide"}
          </Button>
          <CopyButton value={token} title="Copy token" />
        </div>
        <p className="text-xs text-muted-foreground">
          Store it as a secret (a Kubernetes Secret, a Worker secret, your CI's secret store), never in a manifest or repository. It can{" "}
          {runner.repos.length ? (
            <>
              read and run CI for <b className="font-mono">{runner.repos.join(", ")}</b>
            </>
          ) : (
            "read and run CI for every repository"
          )}
          , and {runner.token_expires ? `expires ${new Date(runner.token_expires).toLocaleDateString()}` : "never expires"}.
        </p>
      </Alert>

      {cluster ? (
        <Step title={`Deploy on ${platformLabel(runner.platform)}`}>
          Keep the token in <code>RUNNER_TOKEN</code>, then run the steps below: they store it as a secret and start the instances
          {reset ? ". Restart them so they pick up the new token" : ""}.
          <Snippet text={`export RUNNER_TOKEN=${token}`} display={`export RUNNER_TOKEN=${shown}`} />
          {deployable(runner.platform) && <Deployment platform={runner.platform} />}
        </Step>
      ) : (
        <>
          <Step title="Start it on a machine">
            With <code>bee-runner</code> from <code>make go</code> (or the release). Add <code>--docker</code> to run jobs that set <code>image:</code> in
            containers.
            <Snippet text={local(token)} display={local(shown)} />
          </Step>
          <Step title="Or in Docker">
            <code>-e BEE_TOKEN</code> passes the token from your shell, so it stays out of the command line and <code>docker inspect</code>.
            <Snippet text={docker(token)} display={docker(shown)} />
          </Step>
        </>
      )}

      <Connection runner={runner} since={runner.token_rotated} />

      <div className="mt-4 flex justify-end">
        <Button variant={reset ? "default" : "primary"} onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

function Step({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4 text-xs text-muted-foreground">
      <b className="block text-sm text-foreground">{title}</b>
      {children}
    </div>
  );
}

function Snippet({ text, display = text }: { text: string; display?: string }) {
  return (
    <div className="mt-2 flex items-start gap-1">
      <Pre className="my-0 min-w-0 flex-1 whitespace-pre-wrap break-all">{display}</Pre>
      <span className="mt-2">
        <CopyButton value={text} title="Copy commands" />
      </span>
    </div>
  );
}

/** Polls until an instance uses the new token, like GitLab's "waiting for the runner to connect". */
function Connection({ runner, since }: { runner: RegisteredRunner; since: number }) {
  const q = useRunners(2_000);
  const reg = q.data?.registered.find((r) => r.id === runner.id);
  const instance = q.data?.runners.filter((i) => i.runner_id === runner.id && i.last_seen >= since).sort((a, b) => b.last_seen - a.last_seen)[0];
  if (!reg)
    return q.data ? (
      <Alert variant="warning" className="mt-4 p-3 text-xs">
        <AlertTitle>
          <AlertIcon /> This runner was deleted.
        </AlertTitle>
      </Alert>
    ) : null;
  if (!instance)
    return (
      <div className="mt-4 flex items-center gap-2 rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        <span className="size-2 animate-pulse rounded-full bg-warning" /> Waiting for the runner to connect… This updates as soon as an instance polls with the
        token.
      </div>
    );
  const status = runnerStatus(
    reg,
    q.data!.runners.filter((i) => i.runner_id === runner.id),
  );
  const wrongPlatform = !!reg.platform && instance.platform !== reg.platform;
  const held = wrongPlatform || reg.paused;
  return (
    <Alert variant={held ? "warning" : "success"} className="mt-4 p-3 text-xs">
      <AlertTitle>
        {held ? <AlertIcon /> : <CheckCircleIcon />} Connected: <span className="font-mono">{instance.name}</span>
      </AlertTitle>
      <div>
        {[
          instance.platform && platformLabel(instance.platform),
          instance.version,
          instance.os && `${instance.os}/${instance.arch}`,
          instance.ip,
          instance.concurrency > 0 && `${instance.concurrency} parallel jobs`,
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>
      {wrongPlatform && (
        <div className="mt-1">
          It reports platform {instance.platform || "none"}, so it gets no CI jobs: start it with <code>--platform {reg.platform}</code>.
        </div>
      )}
      {reg.paused && <div className="mt-1">{status.hint}</div>}
    </Alert>
  );
}
